import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PackageOpen } from 'lucide-react';
import nurseryService from '../../services/nursery';
import { formatDateInTz, useOrgTimezone } from '../../utils/timezone';
import { errorMessage } from '../../utils/apiResponse';
import type { NurseryOrder, NurseryOrderStatus } from '../../types/nursery';

/** Confirmed and not yet being picked: trees can be drawn off it (the server's DRAWABLE_STATUSES). */
const DRAWABLE: NurseryOrderStatus[] = ['confirmed', 'partially_allocated', 'allocated'];
/** Before picking starts an order can be held for draw-down. */
const HOLDABLE: NurseryOrderStatus[] = ['draft', ...DRAWABLE];

/** What a draw-down left on the page it opened (OrderDrawDown navigates with it). */
interface DrawnDownState { drawnDown?: { from: string; trees: number; completed: boolean } }

export interface OrderDrawdownCardProps {
  order: NurseryOrder;
  canManage: boolean;
  /** The order changed on the server; read it again. */
  onChanged: () => void;
}

/**
 * Drawing down a bulk order, on the order page (Rob, 2026-10-19).
 *
 * On a bulk order: "Draw down", the switch that holds it off the despatch
 * planner and loads until it is drawn down, and the orders already drawn off
 * it. On a draw-down: the bulk order it came from, and what was just drawn.
 * Renders nothing on an order that is neither, for someone who cannot draw.
 */
export function OrderDrawdownCard({ order, canManage, onChanged }: OrderDrawdownCardProps) {
  const { t } = useTranslation('nursery', { keyPrefix: 'OrderDrawdownCard' });
  const tz = useOrgTimezone();
  const location = useLocation();
  const justDrawn = (location.state as DrawnDownState | null)?.drawnDown;
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const drawdowns = order.drawdowns || [];
  const held = !!order.held_for_drawdown;
  const canDraw = canManage && DRAWABLE.includes(order.status) && order.total_trees > 0;
  const canHold = canManage && HOLDABLE.includes(order.status) && (held || !order.load_id);

  const setHeld = async (next: boolean) => {
    setSaving(true);
    setError(null);
    try {
      const res = await nurseryService.updateOrder(order.id, { held_for_drawdown: next });
      if (!res.success) throw res;
      onChanged();
    } catch (e) {
      setError(errorMessage(e, t('holdFailed')));
    } finally {
      setSaving(false);
    }
  };

  const fromBulk = order.drawn_from_order_id && order.drawn_from_order_number;
  if (!fromBulk && !justDrawn && !drawdowns.length && !held && !canDraw && !canHold) return null;

  return (
    <div className="space-y-3">
      {justDrawn && (
        <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3 text-sm text-green-900 break-words" role="status">
          {t('justDrawn', { count: justDrawn.trees, from: justDrawn.from })}
          {justDrawn.completed && <span> {t('justDrawnAll', { from: justDrawn.from })}</span>}
        </div>
      )}

      {fromBulk && (
        <div className="bg-white border border-neutral-200 rounded-xl px-5 py-3 text-sm text-neutral-700 break-words">
          {t('drawnFrom')}{' '}
          <Link to={`/nursery/orders/${order.drawn_from_order_id}`} className="font-medium text-primary-700 hover:underline">
            {order.drawn_from_order_number}
          </Link>
          {'. '}{t('drawnFromNote')}
        </div>
      )}

      {(canDraw || canHold || held || drawdowns.length > 0) && (
        <div className="bg-white border border-neutral-200 rounded-xl p-5 space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-sm font-semibold text-neutral-600 uppercase tracking-wide">{t('heading')}</h3>
            {canDraw && (
              <Link
                to={`/nursery/orders/${order.id}/draw-down`}
                className="inline-flex items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700"
              >
                <PackageOpen className="h-4 w-4" /> {t('drawDown')}
              </Link>
            )}
          </div>

          <p className="text-sm text-neutral-600 break-words">{held ? t('heldNote') : t('intro')}</p>

          {canHold && (
            <label className="flex items-start gap-2 text-sm text-neutral-800 cursor-pointer">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
                checked={held}
                disabled={saving}
                onChange={(e) => setHeld(e.target.checked)}
              />
              <span className="min-w-0 break-words">{t('holdLabel')}</span>
            </label>
          )}
          {!held && order.load_id && canManage && HOLDABLE.includes(order.status) && (
            <p className="text-xs text-neutral-500 break-words">{t('onLoad', { load: order.load_number ?? '' })}</p>
          )}
          {error && <p className="text-sm text-red-600 break-words" role="alert">{error}</p>}

          {drawdowns.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-xs font-semibold text-neutral-500 uppercase tracking-wide">{t('drawnOff')}</h4>
              {drawdowns.map((d) => (
                <Link
                  key={d.order_id}
                  to={`/nursery/orders/${d.order_id}`}
                  className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 border border-neutral-100 rounded-lg px-4 py-3 hover:border-primary-300"
                >
                  <span className="text-sm font-medium text-primary-700 break-words">{d.order_number}</span>
                  <span className="text-sm text-neutral-600 break-words">
                    {t('drawnTrees', { count: d.trees })} · {formatDateInTz(d.drawn_at, tz)}
                    {d.drawn_by && <span> · {d.drawn_by}</span>}
                  </span>
                </Link>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default OrderDrawdownCard;
